import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { PedidosNuevosAPI } from '../services/pedidosNuevosService';
import { PedidoDetallado, EstadoPedidoNuevo } from '../types/pedidosNuevos';
import EstadoBadge from '../components/EstadoBadge';
import BarraProgreso from '../components/BarraProgreso';
import ConfirmModal from '../components/modals/ConfirmModal';
import { formatearFechaMedia } from '../utils/dateUtils';
import {
  ArrowLeft,
  Calendar,
  User,
  Mail,
  Phone,
  MapPin,
  ChefHat,
  Package,
  FileText,
  Edit,
  CheckCircle,
  XCircle,
  Truck,
  Clock,
} from 'lucide-react';

/**
 * Página de detalle de un pedido individual
 * Muestra información completa y permite cambios de estado según rol
 */
const DetallePedido: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pedido, setPedido] = useState<PedidoDetallado | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [procesando, setProcesando] = useState(false);

  // Estados para modales
  const [modalConfirm, setModalConfirm] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning' as 'warning' | 'danger' | 'info',
    onConfirm: () => {},
  });
  const [modalMensaje, setModalMensaje] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info' as 'warning' | 'danger' | 'info',
  });

  const cargarPedido = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const data = await PedidosNuevosAPI.obtenerDetalle(parseInt(id));
      setPedido(data);
    } catch (err: any) {
      console.error('Error al cargar pedido:', err);
      setError(err.message || 'Error al cargar el pedido');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarPedido();
  }, [id]);

  const handleCambiarEstado = async (nuevoEstado: EstadoPedidoNuevo) => {
    if (!pedido || !id) return;

    // Validar progreso antes de marcar como listo
    if (
      nuevoEstado === 'listo' &&
      pedido.estado === 'en_preparacion' &&
      pedido.progreso &&
      pedido.progreso.porcentaje < 100
    ) {
      setModalMensaje({
        isOpen: true,
        title: 'Pedido Incompleto',
        message: `Este pedido está al ${pedido.progreso.porcentaje}% de completado. Debe finalizar todas las recetas en cocina antes de marcarlo como listo.`,
        type: 'warning',
      });
      return;
    }

    const confirmaciones: Record<EstadoPedidoNuevo, string> = {
      borrador: '¿Regresar el pedido a borrador?',
      solicitado: '¿Confirmar y solicitar este pedido?',
      en_preparacion: '¿Enviar el pedido a cocina?',
      listo: '¿Marcar el pedido como listo para despacho?',
      despachado: '¿Confirmar que el pedido fue despachado?',
      cancelado: '¿Cancelar este pedido? Esta acción no se puede deshacer.',
    };

    const titulos: Record<EstadoPedidoNuevo, string> = {
      borrador: 'Regresar a Borrador',
      solicitado: 'Solicitar Pedido',
      en_preparacion: 'Enviar a Cocina',
      listo: 'Marcar como Listo',
      despachado: 'Confirmar Despacho',
      cancelado: 'Cancelar Pedido',
    };

    // Mostrar modal de confirmación
    setModalConfirm({
      isOpen: true,
      title: titulos[nuevoEstado],
      message: confirmaciones[nuevoEstado],
      type: nuevoEstado === 'cancelado' ? 'danger' : 'warning',
      onConfirm: async () => {
        setProcesando(true);
        try {
          await PedidosNuevosAPI.cambiarEstado(parseInt(id), nuevoEstado);
          await cargarPedido(); // Recargar
          setModalMensaje({
            isOpen: true,
            title: 'Éxito',
            message: 'Estado actualizado correctamente',
            type: 'info',
          });
        } catch (err: any) {
          setModalMensaje({
            isOpen: true,
            title: 'Error',
            message: err.message || 'Error al cambiar estado',
            type: 'danger',
          });
        } finally {
          setProcesando(false);
        }
      },
    });
  };

  // Helper compartido — evita el desfase TZ del formateo directo.
  const formatearFecha = formatearFechaMedia;

  const formatearFechaHora = (fecha: string) => {
    try {
      return new Date(fecha).toLocaleString('es-CL', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return fecha;
    }
  };

  // Determinar acciones disponibles según estado y rol
  const getAccionesDisponibles = (): {
    estado: EstadoPedidoNuevo;
    label: string;
    color: string;
  }[] => {
    if (!pedido || !user) return [];

    const acciones: {
      estado: EstadoPedidoNuevo;
      label: string;
      color: string;
    }[] = [];

    // Cliente puede confirmar borradores o modificar solicitados
    if (user.rol === 'cliente' && user.id === pedido.cliente_id) {
      if (pedido.estado === 'borrador') {
        acciones.push({
          estado: 'solicitado',
          label: 'Confirmar Pedido',
          color: 'blue',
        });
      }
      if (pedido.estado === 'solicitado') {
        acciones.push({
          estado: 'borrador',
          label: 'Modificar',
          color: 'gray',
        });
      }
    }

    // Admin/Chef pueden aprobar y avanzar estados
    if (user.rol === 'administrador' || user.rol === 'chef') {
      if (pedido.estado === 'solicitado') {
        acciones.push({
          estado: 'en_preparacion',
          label: 'Aprobar y Enviar a Cocina',
          color: 'green',
        });
        acciones.push({
          estado: 'cancelado',
          label: 'Cancelar',
          color: 'red',
        });
      }
      if (pedido.estado === 'en_preparacion') {
        acciones.push({
          estado: 'listo',
          label: 'Marcar como Listo',
          color: 'green',
        });
      }
      if (pedido.estado === 'listo') {
        acciones.push({
          estado: 'despachado',
          label: 'Confirmar Despacho',
          color: 'purple',
        });
      }
    }

    // Cocina puede marcar como listo
    if (user.rol === 'cocina') {
      if (pedido.estado === 'en_preparacion') {
        acciones.push({
          estado: 'listo',
          label: 'Marcar como Listo',
          color: 'green',
        });
      }
    }

    return acciones;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Cargando pedido...</p>
        </div>
      </div>
    );
  }

  if (error || !pedido) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <button
          onClick={() => navigate('/pedidos')}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Volver a Pedidos</span>
        </button>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-8 text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-red-800 dark:text-red-200 mb-2">
            Error al cargar el pedido
          </h2>
          <p className="text-red-600 dark:text-red-300">
            {error || 'El pedido no existe o no tienes permisos para verlo'}
          </p>
        </div>
      </div>
    );
  }

  const acciones = getAccionesDisponibles();

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header con navegación */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/pedidos')}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Volver a Pedidos</span>
        </button>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Pedido {pedido.codigo}
              </h1>
              <EstadoBadge estado={pedido.estado} size="lg" />
            </div>
            <p className="text-gray-600 dark:text-gray-400">
              Creado el {formatearFechaHora(pedido.created_at)}
            </p>
          </div>

          {/* Botones de acción */}
          {acciones.length > 0 && (
            <div className="flex gap-2">
              {acciones.map((accion) => {
                const colorClasses = {
                  blue: 'bg-blue-600 hover:bg-blue-700 text-white',
                  green: 'bg-green-600 hover:bg-green-700 text-white',
                  red: 'bg-red-600 hover:bg-red-700 text-white',
                  gray: 'bg-gray-600 hover:bg-gray-700 text-white',
                  purple: 'bg-purple-600 hover:bg-purple-700 text-white',
                };

                return (
                  <button
                    key={accion.estado}
                    onClick={() => handleCambiarEstado(accion.estado)}
                    disabled={procesando}
                    className={`
                      flex items-center gap-2 px-4 py-2 rounded-lg transition-colors
                      disabled:opacity-50 disabled:cursor-not-allowed
                      ${colorClasses[accion.color as keyof typeof colorClasses]}
                    `}
                  >
                    {accion.estado === 'solicitado' && (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    {accion.estado === 'en_preparacion' && (
                      <ChefHat className="w-4 h-4" />
                    )}
                    {accion.estado === 'listo' && (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    {accion.estado === 'despachado' && (
                      <Truck className="w-4 h-4" />
                    )}
                    {accion.estado === 'cancelado' && (
                      <XCircle className="w-4 h-4" />
                    )}
                    {accion.estado === 'borrador' && (
                      <Edit className="w-4 h-4" />
                    )}
                    <span>{accion.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna Principal - Detalles del Pedido */}
        <div className="lg:col-span-2 space-y-6">
          {/* Información de la Minuta */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <ChefHat className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Información de la Minuta
              </h2>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Minuta
                </p>
                <p className="text-lg font-medium text-gray-900 dark:text-white">
                  {pedido.minuta_nombre}
                </p>
              </div>

              {pedido.minuta_descripcion && (
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Descripción
                  </p>
                  <p className="text-gray-700 dark:text-gray-300">
                    {pedido.minuta_descripcion}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Periodo
                  </p>
                  <p className="text-gray-900 dark:text-white">
                    {formatearFecha(pedido.minuta_fecha_inicio)} -{' '}
                    {formatearFecha(pedido.minuta_fecha_fin)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Fecha de Entrega
                  </p>
                  <p className="text-gray-900 dark:text-white font-medium">
                    {formatearFecha(pedido.fecha_entrega)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Indicador de Transcripción */}
          {!!pedido.es_transcripcion && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                <div>
                  <p className="font-medium text-yellow-800 dark:text-yellow-200">
                    Pedido Transcrito
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    Este pedido fue creado por{' '}
                    <span className="font-medium">
                      {pedido.creado_por_nombre}
                    </span>{' '}
                    ({pedido.creado_por_email}) a partir de un pedido recibido
                    por correo o teléfono.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Detalle de Recetas */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Detalle del Pedido
              </h2>
            </div>

            {pedido.detalles.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                No hay recetas en este pedido
              </p>
            ) : (
              <div className="space-y-3">
                {pedido.detalles.map((detalle) => (
                  <div
                    key={detalle.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {detalle.receta_nombre}
                        </h3>
                        {detalle.tipo_plato_nombre && (
                          <span
                            className="px-2 py-0.5 text-xs font-medium rounded"
                            style={{
                              backgroundColor: detalle.tipo_plato_color
                                ? `${detalle.tipo_plato_color}20`
                                : '#e5e7eb',
                              color: detalle.tipo_plato_color || '#6b7280',
                            }}
                          >
                            {detalle.tipo_plato_nombre}
                          </span>
                        )}
                      </div>
                      {detalle.receta_descripcion && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {detalle.receta_descripcion}
                        </p>
                      )}
                    </div>
                    <div className="ml-4 flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {detalle.cantidad}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          porciones
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Resumen */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between text-lg font-semibold">
                <span className="text-gray-900 dark:text-white">
                  Total de porciones:
                </span>
                <span className="text-blue-600 dark:text-blue-400">
                  {pedido.total_platos}
                </span>
              </div>
            </div>
          </div>

          {/* Progreso de Cocina (solo para pedidos en preparación) */}
          {pedido.estado === 'en_preparacion' && pedido.progreso && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Progreso de Preparación
                </h2>
              </div>

              {/* Barra de progreso global */}
              <div className="mb-6">
                <BarraProgreso
                  porcentaje={pedido.progreso.porcentaje}
                  porcionesCompletadas={pedido.progreso.porciones_completadas}
                  porcionesTotales={pedido.progreso.porciones_totales}
                  size="lg"
                />
              </div>

              {/* Desglose por receta */}
              {pedido.desglose_cocina && pedido.desglose_cocina.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Detalle por Receta
                  </h3>
                  <div className="space-y-2">
                    {pedido.desglose_cocina.map((receta, index) => {
                      const estadoIcons = {
                        pendiente: '⏳',
                        en_preparacion: '🔄',
                        terminado: '✅',
                        despachado: '📦',
                      };

                      const estadoColors = {
                        pendiente: 'text-gray-500 dark:text-gray-400',
                        en_preparacion: 'text-yellow-600 dark:text-yellow-400',
                        terminado: 'text-green-600 dark:text-green-400',
                        despachado: 'text-blue-600 dark:text-blue-400',
                      };

                      const estadoLabels = {
                        pendiente: 'Pendiente',
                        en_preparacion: 'En Preparación',
                        terminado: 'Terminado',
                        despachado: 'Despachado',
                      };

                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <span className="text-2xl">
                              {estadoIcons[receta.estado]}
                            </span>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {receta.receta_nombre}
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {receta.cantidad} porciones
                              </p>
                            </div>
                          </div>
                          <span
                            className={`text-sm font-medium ${estadoColors[receta.estado]}`}
                          >
                            {estadoLabels[receta.estado]}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Mensaje informativo */}
              {pedido.progreso.porcentaje < 100 && (
                <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    <strong>Nota:</strong> El pedido solo podrá marcarse como
                    "listo" cuando todas las recetas estén terminadas.
                  </p>
                </div>
              )}

              {pedido.progreso.porcentaje === 100 && (
                <div className="mt-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <p className="text-sm text-green-800 dark:text-green-200">
                    <CheckCircle className="w-4 h-4 inline mr-1" />
                    <strong>¡Pedido completo!</strong> Todas las recetas están
                    terminadas. Puedes marcarlo como listo para despacho.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Observaciones */}
          {pedido.observaciones && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Observaciones
              </h2>
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {pedido.observaciones}
              </p>
            </div>
          )}
        </div>

        {/* Columna Lateral - Información del Cliente */}
        <div className="space-y-6">
          {/* Info del Cliente */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Cliente
              </h2>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Código Cliente
                </p>
                <p className="font-mono font-semibold text-blue-600 dark:text-blue-400">
                  {pedido.cliente_codigo || 'N/A'}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Razón Social
                </p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {pedido.cliente_razon_social}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Nombre
                </p>
                <p className="text-gray-900 dark:text-white">
                  {pedido.cliente_nombre}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <a
                  href={`mailto:${pedido.cliente_email}`}
                  className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  {pedido.cliente_email}
                </a>
              </div>

              {pedido.cliente_telefono && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <a
                    href={`tel:${pedido.cliente_telefono}`}
                    className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    {pedido.cliente_telefono}
                  </a>
                </div>
              )}

              {pedido.cliente_direccion && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                  <p className="text-gray-700 dark:text-gray-300">
                    {pedido.cliente_direccion}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Fechas */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Fechas
              </h2>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Creado
                </p>
                <p className="text-gray-900 dark:text-white">
                  {formatearFechaHora(pedido.created_at)}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Última actualización
                </p>
                <p className="text-gray-900 dark:text-white">
                  {formatearFechaHora(pedido.updated_at)}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Fecha de pedido
                </p>
                <p className="text-gray-900 dark:text-white">
                  {formatearFecha(pedido.fecha_pedido)}
                </p>
              </div>

              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                  Fecha de entrega
                </p>
                <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                  {formatearFecha(pedido.fecha_entrega)}
                </p>
              </div>
            </div>
          </div>

          {/* Estadísticas */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Estadísticas
              </h2>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  Total Porciones
                </span>
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  {pedido.total_platos}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  Recetas Distintas
                </span>
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  {pedido.total_items}
                </span>
              </div>

              {/* Costo del pedido — solo admin (TD-025). Runtime, no
                  congelado al despachar (ver TD-027). */}
              {user?.rol === 'administrador' && (
                <div
                  className="flex items-center justify-between"
                  title="Costo interno de producción (ingredientes × cantidades). No es precio de venta."
                >
                  <span className="text-gray-600 dark:text-gray-400">
                    Costo del pedido
                  </span>
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    {new Intl.NumberFormat('es-CL', {
                      style: 'currency',
                      currency: 'CLP',
                      maximumFractionDigits: 0,
                    }).format(pedido.costo_pedido ?? 0)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Confirmación */}
      <ConfirmModal
        isOpen={modalConfirm.isOpen}
        onClose={() => setModalConfirm({ ...modalConfirm, isOpen: false })}
        onConfirm={modalConfirm.onConfirm}
        title={modalConfirm.title}
        message={modalConfirm.message}
        type={modalConfirm.type}
        confirmText="Confirmar"
        cancelText="Cancelar"
      />

      {/* Modal de Mensaje */}
      <ConfirmModal
        isOpen={modalMensaje.isOpen}
        onClose={() => setModalMensaje({ ...modalMensaje, isOpen: false })}
        onConfirm={() => setModalMensaje({ ...modalMensaje, isOpen: false })}
        title={modalMensaje.title}
        message={modalMensaje.message}
        type={modalMensaje.type}
        confirmText="Aceptar"
        cancelText=""
      />
    </div>
  );
};

export default DetallePedido;
